import { useToggle } from 'ahooks';
import { Search as SearchIcon, X, ChevronDown } from 'lucide-react';
import { useAppDisplayModeContext } from '@archipro-design/aria';

import { Sheet, SheetContent } from '@rocco/components/sheet';
import { PillButton } from '@rocco/components/pill-button';
import { Button } from '@rocco/components/button';
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '@rocco/components/popover';
import { RadioGroup, RadioGroupItem } from '@rocco/components/radio-group';
import { cn } from '@rocco/utils/cn';
import { RefineIcon } from '@modules/professional/component/shared/RefineIcon';

interface ItemsBarSortOption {
    value: string;
    label: string;
}

interface ProfessionalItemsBarProps {
    /** Sort options — a Refine sheet on mobile, a Sort By dropdown on desktop. */
    sortOptions: ItemsBarSortOption[];
    /** The active sort value. */
    activeSort: string;
    /** Apply a sort (navigates or sets state — the caller decides). */
    onSort: (value: string) => void;
    /** Search value; omit `onSearchChange` to hide the search entirely. */
    searchValue?: string;
    onSearchChange?: (value: string) => void;
    searchPlaceholder?: string;
    /** `dark` inverts the controls for a dark tab. */
    theme?: 'light' | 'dark';
}

/** The Refine sliders glyph — matches the category page's pill-bar icon. */
/**
 * The compact bar for the sort-only item tabs (Videos, Downloads). Desktop
 * mirrors the ES-backed grid tabs' bar — a revamp search pill on the left and a
 * "Sort By" dropdown on the right; mobile keeps the icon-only Refine circle that
 * opens a sort bottom sheet beside an always-expanded pill search.
 */
export const ProfessionalItemsBar = ({
    sortOptions,
    activeSort,
    onSort,
    searchValue,
    onSearchChange,
    searchPlaceholder = 'Search',
    theme = 'light',
}: ProfessionalItemsBarProps) => {
    const [sortOpen, { set: setSortOpen }] = useToggle(false);
    const { desktop } = useAppDisplayModeContext();
    const isDark = theme === 'dark';

    if (desktop) {
        return (
            <div className="flex items-center justify-between gap-4">
                {onSearchChange ? (
                    <div
                        className={cn(
                            'flex w-[32rem] max-w-full items-center justify-between rounded-full py-[0.5625rem] pr-[0.5625rem] pl-[1.8125rem]',
                            isDark ? 'bg-white/[0.08]' : 'bg-[#f6f6f6]',
                        )}
                    >
                        <div className="flex min-w-0 flex-1 items-center gap-[0.6875rem]">
                            <SearchIcon
                                className={cn(
                                    'size-[1.375rem] shrink-0',
                                    isDark
                                        ? 'text-white/40'
                                        : 'text-[#1f1f21]/40',
                                )}
                            />
                            <input
                                type="search"
                                value={searchValue ?? ''}
                                onChange={(event) =>
                                    onSearchChange(event.target.value)
                                }
                                placeholder={searchPlaceholder}
                                className={cn(
                                    'w-full min-w-0 border-0 bg-transparent text-[1.375rem] leading-none tracking-[-0.0138rem] outline-none [&::-webkit-search-cancel-button]:appearance-none',
                                    isDark
                                        ? 'text-white placeholder:text-white/40'
                                        : 'text-[#1f1f21] placeholder:text-[#1f1f21]/40',
                                )}
                            />
                        </div>
                        <button
                            type="button"
                            aria-label={searchValue ? 'Clear search' : 'Search'}
                            onClick={() => searchValue && onSearchChange('')}
                            className={cn(
                                'flex size-[2.5rem] shrink-0 items-center justify-center rounded-full transition-colors',
                                isDark
                                    ? 'bg-white/[0.14] text-white hover:bg-white/20'
                                    : 'bg-[#181818] text-white hover:bg-[#2d2d2d]',
                            )}
                        >
                            {searchValue ? (
                                <X className="size-[1.05rem]" />
                            ) : (
                                <SearchIcon className="size-[1.05rem]" />
                            )}
                        </button>
                    </div>
                ) : (
                    <span />
                )}
                {sortOptions.length > 0 ? (
                    <DesktopSortDropdown
                        sortOptions={sortOptions}
                        activeSort={activeSort}
                        onSort={onSort}
                        isDark={isDark}
                    />
                ) : null}
            </div>
        );
    }

    return (
        <>
            <div className="flex items-center gap-2">
                <button
                    type="button"
                    aria-label="Refine"
                    onClick={() => setSortOpen(true)}
                    className={cn(
                        'flex size-13 shrink-0 items-center justify-center rounded-full transition-colors',
                        isDark
                            ? 'bg-white text-[#181818]'
                            : 'bg-[#181818] text-white',
                    )}
                >
                    <RefineIcon />
                </button>

                {onSearchChange ? (
                    <div
                        className={cn(
                            'flex min-w-0 flex-1 items-center gap-2 rounded-full px-4 py-3',
                            isDark ? 'bg-[#323232]' : 'bg-[#f2f2f2]',
                        )}
                    >
                        <SearchIcon
                            className={cn(
                                'size-[1.125rem] shrink-0',
                                isDark ? 'text-white/70' : 'text-[#767676]',
                            )}
                        />
                        <input
                            type="search"
                            value={searchValue ?? ''}
                            onChange={(event) =>
                                onSearchChange(event.target.value)
                            }
                            placeholder={searchPlaceholder}
                            className={cn(
                                'w-full bg-transparent font-sans text-sm outline-none',
                                isDark
                                    ? 'text-white placeholder:text-white/50'
                                    : 'text-[#1f1f21] placeholder:text-[#767676]',
                            )}
                        />
                    </div>
                ) : null}
            </div>

            <Sheet open={sortOpen} onOpenChange={setSortOpen}>
                <SheetContent
                    side="bottom"
                    color="opaque"
                    hideDefaultCloseButton
                >
                    <div className="flex w-dvw flex-col">
                        <div className="relative flex min-h-14 items-center justify-center border-b border-black/10 px-5">
                            <h2 className="m-0 font-sans text-lg font-medium text-[#181818]">
                                Refine
                            </h2>
                            <button
                                type="button"
                                aria-label="Close"
                                onClick={() => setSortOpen(false)}
                                className="absolute right-3 flex size-10 items-center justify-center text-[#181818] opacity-70 transition-opacity hover:opacity-100"
                            >
                                <X className="size-6" />
                            </button>
                        </div>
                        <div className="px-5 py-4">
                            <div className="flex min-h-11 items-center font-sans text-base font-medium text-[#181818]">
                                Sort by
                            </div>
                            <div className="flex flex-wrap gap-2 pt-1 pb-2">
                                {sortOptions.map((option) => (
                                    <PillButton
                                        key={option.value}
                                        size="sm"
                                        selected={option.value === activeSort}
                                        onClick={() => {
                                            onSort(option.value);
                                            setSortOpen(false);
                                        }}
                                    >
                                        {option.label}
                                    </PillButton>
                                ))}
                            </div>
                        </div>
                    </div>
                </SheetContent>
            </Sheet>
        </>
    );
};

/**
 * Desktop "Sort By" dropdown — mirrors the grid tabs' sort control (Rocco
 * popover + radio list) so the sort-only tabs read the same across the page.
 */
const DesktopSortDropdown = ({
    sortOptions,
    activeSort,
    onSort,
    isDark,
}: {
    sortOptions: ItemsBarSortOption[];
    activeSort: string;
    onSort: (value: string) => void;
    isDark: boolean;
}) => {
    const [open, { set: setOpen }] = useToggle(false);
    const activeOption = sortOptions.find(
        (option) => option.value === activeSort,
    );
    // The first option is the default — show the neutral "Sort By" label until
    // the user picks something else, matching the grid tabs' sort dropdown.
    const isDefault =
        !activeOption || activeOption.value === sortOptions[0]?.value;

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <Button
                    color="transparent"
                    size="compact"
                    weight="normal"
                    className={cn('group shrink-0', isDark && 'text-white')}
                >
                    <span>{isDefault ? 'Sort By' : activeOption?.label}</span>
                    <ChevronDown className="ml-2 size-4.5 transition-transform group-data-[state=open]:rotate-180" />
                </Button>
            </PopoverTrigger>
            <PopoverContent align="end" paddings="small">
                <RadioGroup
                    className="gap-0"
                    value={activeSort}
                    onValueChange={(value) => {
                        onSort(value);
                        setOpen(false);
                    }}
                >
                    {sortOptions.map((option) => (
                        <label
                            key={option.value}
                            className="flex cursor-pointer items-center space-x-3 rounded-2 px-2 py-2.5 hover:bg-gray-50"
                        >
                            <RadioGroupItem value={option.value} size="small" />
                            <span className="text-base">{option.label}</span>
                        </label>
                    ))}
                </RadioGroup>
            </PopoverContent>
        </Popover>
    );
};
